import { useMemo } from 'react'
import { translateNameElementChips } from '../../../utils/translationUtils'
import { superDeduper } from '../components/editPanels/utils/editPanelDispatchBuilders'

/**
 * Go through all the subelements of an element and get the ones that match the chip data
 * This allows for multiple fields of one chip type to be added
 * @param {Object} element - the super element like Name
 * @param {Array} filteredNameFieldChipData - the types of fields we are looking for
 * @returns Array of fields with type data included
 */
const getSelectedFieldData = (element, filteredNameFieldChipData) => {
  let selectedFieldData = []
  // go through each subelement and find the ones that match the chips, no matter how many there are
  if (element && element.subElements) {
    element.subElements.forEach((se) => {
      const filteredNameFieldChipValues = filteredNameFieldChipData.flatMap((chipData) => chipData.value)
      if (se?.subElements) {
        se.subElements.forEach((field) => {
          if (filteredNameFieldChipValues.includes(field?.fieldType)) {
            const fieldChipData = filteredNameFieldChipData.filter(
              (data) => data.value === se.subElements[0].fieldType
            )[0]
            selectedFieldData.push({
              ...fieldChipData,
              ...field,
            })
          }
        })
      }
    })
  }
  selectedFieldData = superDeduper(selectedFieldData)
  return selectedFieldData
}

/**
 * Filter to the types that are a part of the name
 * @param {Object} element - the super element like Name
 * @param {Object} nameFieldChipData
 * @returns
 */
const filterChipData = (element, nameFieldChipData) => {
  return nameFieldChipData?.filter((chip) => {
    const foundAllowedFieldForChip = element?.subElements?.find((se) =>
      se?.subElements?.find((field) => chip.value === field.fieldType)
    )
    return foundAllowedFieldForChip
  })
}

export default function useTreatedNameFieldChips(element, types, selectedFields, selectedTempChips, lockedTempChips) {
  const nameFieldChipData = useMemo(() => translateNameElementChips(types), [types])
  const selectedFieldData = useMemo(
    () => getSelectedFieldData(element, filterChipData(element, nameFieldChipData)),
    [element, nameFieldChipData]
  )
  const treatedNameFieldChipData = useMemo(() => {
    const chipsInUse = selectedTempChips.concat(lockedTempChips)
    // keep in sync the disabled/enabled state of each name field chip (can only select each one once)
    // get an array of fieldTypes from chips the user has selected
    const selectedTempChipFieldTypes = chipsInUse.map((f) => f?.value)
    // attach fieldType to chips from pre-existing fields
    const enhancedSelectedTempChips = chipsInUse.map((c) => ({
      ...c,
      fieldType: selectedFieldData.find((f) => f?.id === c?.id)?.fieldType,
    }))
    // name chips added while on this panel (not saved in SLS yet)
    const addedNameFieldTypes = selectedFields.filter((field) => field.added).map((field) => field.fieldType)

    const disabledFieldTypeMap = ['NAME_SURN', 'NAME_SUF', 'NAME_GN', 'NAME_PRE', 'NAME_SPRE', 'NAME_PATR', 'NAME_MATR']
    return nameFieldChipData?.map((chip) => {
      const chipFieldType = selectedTempChipFieldTypes.includes(chip.value)
        ? chip.value
        : enhancedSelectedTempChips.find((f) => f?.fieldType === chip?.value)?.fieldType
      return {
        ...chip,
        disabled: disabledFieldTypeMap.includes(chipFieldType) || addedNameFieldTypes.includes(chip.value),
      }
    })
  }, [selectedFields, nameFieldChipData, selectedFieldData, selectedTempChips, lockedTempChips])
  return { treatedNameFieldChipData, selectedFieldData }
}

export function disableSaveFuncRequireGnOrSurn(currentChips, t) {
  return {
    disabled: !currentChips.find(
      (c) => c.value.split(':')?.[0] === 'NAME_GN' || c.value.split(':')?.[0] === 'NAME_SURN'
    ),
    message: t('field-selector-require-name', 'You must select a given name or a surname'),
  }
}

export function disableSaveFuncRequireOneField(currentChips, t) {
  return {
    disabled: !currentChips.length,
    message: t('field-selector-require-one-field', 'You must select at least one field to continue'),
  }
}
